/**
 * Shared Utilities: Search Result Cards
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R09
 *
 * Common utility functions used across all search result card components.
 * Extracted to eliminate duplication across SemanticSearchPage,
 * GalenSearchPage, and SimpleSearchPage (Bug #3870).
 */

/** Strip HTML/XML tags from a string. */
export function stripHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, '').trim();
}

/**
 * True when the HTML carries block-level structured markup (a table or figure)
 * that must be rendered as HTML rather than flattened to a run-on text blob.
 *
 * Bug #5058: the excerpt path stripped all tags, so table/figure content
 * (FiguresAndTables, table-bearing sections) collapsed into "Name of TestBody
 * PartDescription..." run-on text. When this returns true the card renders the
 * raw HTML through SafeHtml so the table/figure structure is preserved, matching
 * the View Full Result / Browse rendering.
 */
export function containsRichBlock(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  return /<(table|figure)\b/i.test(value);
}

/**
 * Normalise smart/curly quotes to straight quotes (Bug #5066).
 *
 * macOS/iOS/Windows smart-quotes, mobile keyboards, and copy-paste from
 * documents turn `"` into `“ ”` (and `'` into `‘ ’`). Azure Search only honours
 * straight `"` as a phrase delimiter, so a curly-quoted phrase silently stops
 * narrowing results. Apply to the query before it is sent to the search API.
 */
export function normalizeQuotes(value: string): string {
  return value.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
}

/** Extract a metadata field as a string, stripping HTML. */
export function getMetaString(metadata: Record<string, unknown>, key: string): string {
  const val = metadata[key];
  if (val === null || val === undefined || val === 'null') return '';
  return stripHtml(val);
}

/** Format an ISO date string to "DD Mon YYYY". */
export function formatPublishDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
